import { useState, useEffect, useMemo } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { feature } from "topojson-client";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MapPin } from "lucide-react";
import IndiaMap from "@/components/IndiaMap";

const WORLD_URL = "/world-countries.json";
const INDIA_URL = "/india-states.geojson";
const INDIA_CENTROID = [79.5, 22.5];

function worldGeoStyle(isActive, isSelected, expOnly = false) {
  return {
    default: {
      fill: isActive ? (isSelected ? "#0C3244" : expOnly ? "rgba(14,61,82,0.35)" : "#76E39A") : "rgba(12,50,68,0.07)",
      stroke: "#0C3244",
      strokeWidth: 0.3,
      strokeOpacity: 0.3,
      outline: "none",
      cursor: isActive ? "pointer" : "default",
      transition: "fill 300ms ease",
    },
    hover: {
      fill: isActive ? "#0C3244" : "rgba(12,50,68,0.14)",
      stroke: "#0C3244",
      strokeWidth: 0.4,
      outline: "none",
    },
    pressed: { fill: "#0C3244", outline: "none" },
  };
}

export function PulsePin({ coordinates, big = false, variant = "prog" }) {
  const exp = variant === "exp";
  return (
    <Marker coordinates={coordinates}>
      <circle r={big ? 11 : 8} className="map-pin-halo" style={exp ? { fill: "rgba(14,61,82,0.30)" } : undefined} />
      <circle r={big ? 3.5 : 2.6} className="map-pin-core" style={exp ? { fill: "#0E3D52", stroke: "#F7F4EA" } : undefined} />
    </Marker>
  );
}

function MapTooltip({ name, count, expCount = 0 }) {
  const parts = [];
  if (count > 0) parts.push(`${count} programme ${count > 1 ? "entries" : "entry"}`);
  if (expCount > 0) parts.push(`${expCount} field ${expCount > 1 ? "experiences" : "experience"}`);
  return (
    <div className="pointer-events-none absolute top-4 left-4 z-10 bg-ptf-primary text-ptf-bg px-4 py-2" data-testid="map-tooltip">
      <p className="font-display uppercase text-lg leading-none tracking-tight">{name}</p>
      <p className="text-[0.65rem] uppercase tracking-[0.16em] text-ptf-accent mt-1">
        {parts.length ? parts.join(" · ") : "No work here yet"}
      </p>
    </div>
  );
}

// countryCounts:     { countryName: n } (stories + programmes — Foundation work)
// stateCounts:       { indianStateName: n }
// experienceCounts:  { countryName: n } (field experiences — mapped separately, never implying a programme)
// selected:          { type: "country" | "state", name } | null
export default function WorldMap({ countryCounts = {}, stateCounts = {}, experienceCounts = {}, selected = null, onSelect }) {
  const [view, setView] = useState("world");
  const [hovered, setHovered] = useState(null);
  const [worldGeo, setWorldGeo] = useState(null);
  const [indiaGeo, setIndiaGeo] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(WORLD_URL)
      .then((r) => r.json())
      .then((topo) => {
        if (!alive) return;
        const geo = feature(topo, topo.objects.countries);
        // India is rendered from the Foundation's authoritative India GeoJSON below,
        // never from the generic world atlas (whose boundary differs).
        setWorldGeo({
          ...geo,
          features: geo.features.filter((f) => f.properties.name !== "Antarctica" && f.properties.name !== "India"),
        });
      });
    fetch(INDIA_URL)
      .then((r) => r.json())
      .then((geo) => { if (alive) setIndiaGeo(geo); });
    return () => { alive = false; };
  }, []);

  const indiaActive = (countryCounts["India"] || 0) + (experienceCounts["India"] || 0) > 0;
  const anyActive = Object.keys(countryCounts).length > 0 || Object.keys(experienceCounts).length > 0;

  const pinCountries = useMemo(
    () => new Set(Object.keys(countryCounts).filter((k) => countryCounts[k] > 0)),
    [countryCounts]
  );
  const expOnlyCountries = useMemo(
    () => new Set(Object.keys(experienceCounts).filter((k) => (countryCounts[k] || 0) === 0 && experienceCounts[k] > 0)),
    [countryCounts, experienceCounts]
  );

  const clickCountry = (name, isActive) => {
    if (name === "India" && isActive) {
      setView("india");
      return;
    }
    if (!isActive || !onSelect) return;
    const same = selected?.type === "country" && selected.name === name;
    onSelect(same ? null : { type: "country", name });
  };

  return (
    <div className="relative w-full">
      <AnimatePresence mode="wait">
        {view === "world" ? (
          <motion.div key="world" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.03 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <div className="overflow-x-auto md:overflow-visible md:mx-auto md:max-w-[880px]" data-testid="world-map-scroll">
              <div className="min-w-[620px] md:min-w-0">
                <ComposableMap
              projection="geoEqualEarth"
              projectionConfig={{ scale: 175, center: [12, 8] }}
              width={960}
              height={480}
              style={{ width: "100%", height: "auto" }}
              data-testid="world-map"
            >
              {worldGeo && (
                <Geographies geography={worldGeo}>
                  {({ geographies }) => (
                    <>
                      {geographies.map((geo) => {
                        const name = geo.properties.name;
                        const count = countryCounts[name] || 0;
                        const expCount = experienceCounts[name] || 0;
                        const isActive = count + expCount > 0;
                        const isSelected = selected?.type === "country" && selected.name === name;
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            data-testid={`map-country-${name}`}
                            onMouseEnter={() => setHovered({ name, count, expCount })}
                            onMouseLeave={() => setHovered(null)}
                            onClick={() => clickCountry(name, isActive)}
                            className={isActive ? "rsm-geo-active" : ""}
                            style={worldGeoStyle(isActive, isSelected, count === 0 && expCount > 0)}
                          />
                        );
                      })}
                      {geographies
                        .filter((geo) => pinCountries.has(geo.properties.name))
                        .map((geo) => (
                          <PulsePin key={`pin-${geo.rsmKey}`} coordinates={geoCentroid(geo)} />
                        ))}
                      {geographies
                        .filter((geo) => expOnlyCountries.has(geo.properties.name))
                        .map((geo) => (
                          <PulsePin key={`exp-pin-${geo.rsmKey}`} coordinates={geoCentroid(geo)} variant="exp" />
                        ))}
                    </>
                  )}
                </Geographies>
              )}
              {/* India drawn from the Foundation's own map so the boundary is complete and consistent */}
              {indiaGeo && (
                <Geographies geography={indiaGeo}>
                  {({ geographies }) => {
                    const indiaCount = countryCounts["India"] || 0;
                    const indiaExp = experienceCounts["India"] || 0;
                    const isSelected = selected?.type === "country" && selected.name === "India";
                    const style = worldGeoStyle(indiaCount + indiaExp > 0, isSelected, indiaCount === 0 && indiaExp > 0);
                    const sealed = {
                      default: { ...style.default, stroke: style.default.fill, strokeWidth: 0.6, strokeOpacity: 1 },
                      hover: { ...style.hover, stroke: style.hover.fill, strokeWidth: 0.6 },
                      pressed: style.pressed,
                    };
                    return geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        data-testid="map-country-India"
                        onMouseEnter={() => setHovered({ name: "India", count: indiaCount, expCount: indiaExp })}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => clickCountry("India", indiaCount + indiaExp > 0)}
                        className={indiaCount + indiaExp > 0 ? "rsm-geo-active" : ""}
                        style={sealed}
                      />
                    ));
                  }}
                </Geographies>
              )}
              {indiaActive && <PulsePin coordinates={INDIA_CENTROID} big variant={(countryCounts["India"] || 0) > 0 ? "prog" : "exp"} />}
                </ComposableMap>
              </div>
            </div>
            <p className="mt-2 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/45 md:hidden">Swipe the map · tap a lit country</p>
            {indiaActive && (
              <button
                type="button"
                data-testid="zoom-india"
                onClick={() => setView("india")}
                className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-secondary hover:text-ptf-primary transition-colors"
              >
                <MapPin className="h-4 w-4" /> Explore India, state by state
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div key="india" className="mx-auto md:max-w-[480px]" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <button
                type="button"
                data-testid="map-back-world"
                onClick={() => setView("world")}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-secondary hover:text-ptf-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> World view
              </button>
              <button
                type="button"
                data-testid="select-all-india"
                onClick={() => onSelect && onSelect({ type: "country", name: "India" })}
                className="text-xs uppercase tracking-[0.14em] text-ptf-primary/60 hover:text-ptf-primary underline underline-offset-4 transition-colors"
              >
                All India work
              </button>
            </div>
            <IndiaMap
              counts={stateCounts}
              selected={selected?.type === "state" ? selected.name : ""}
              onSelect={(s) => onSelect && onSelect(s ? { type: "state", name: s } : null)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {view === "world" && hovered && <MapTooltip name={hovered.name} count={hovered.count} expCount={hovered.expCount || 0} />}

      {view === "world" && !anyActive && (
        <p className="mt-4 text-center text-sm italic font-serif-ed text-ptf-text/50">
          Countries will light up here as real programmes and stories begin.
        </p>
      )}
    </div>
  );
}
